import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  CreateLeadMutation,
  CreateLeadMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { LeadEntityType } from "lib/types/graphQLEnums";
import { isBuyerOrganisation } from "../../../../components/entities/utils";
import { EventData, EventNames, useTracking } from "../../../tracking";
import { updateCachedDocumentsHasBuyerLeads } from "../documents/utils";
import { updateCachedNoticesHasBuyerLeads } from "../notices/utils";
import { useGraphQlMutation } from "../useGraphQlClient";
import { updateLeadEntityCache } from "./update_cache/updateLeadEntityCache";

const CREATE_LEAD_QUERY = graphql(`
  mutation createLead($lead: CreateLeadRequest!, $entities: LeadEntitiesInput!) {
    createLead(CreateLeadRequest: $lead, entities: $entities) {
      id
      name
      value
      stage {
        name
      }
    }
  }
`);

export function useCreateLead(
  trackingData: EventData,
  options?: UseMutationOptions<CreateLeadMutation, unknown, CreateLeadMutationVariables, unknown>,
) {
  const queryClient = useQueryClient();

  const { logEvent } = useTracking();

  const { data, ...rest } = useGraphQlMutation(CREATE_LEAD_QUERY, {
    ...options,
    onSuccess: async (data, variables, context) => {
      await queryClient.refetchQueries({ queryKey: ["leads"] });

      // If entities have been created, then update the cache for leadsSavedToEntities query and the entities themselves
      if (variables.entities.entities.length > 0) {
        const entities = variables.entities.entities.map((entity) => ({
          entityId: entity.entityId,
          entityType: entity.entityType,
          leadId: data.createLead.id,
          leadName: data.createLead.name,
        }));
        await updateLeadEntityCache({
          queryClient,
          isAdding: true,
          entities,
        });

        const buyerIds = entities.filter(isBuyerOrganisation).map((e) => e.entityId);

        updateCachedNoticesHasBuyerLeads(queryClient, buyerIds);
        updateCachedDocumentsHasBuyerLeads(queryClient, buyerIds);
      }

      const { id, name, value, stage } = data.createLead;
      const hasCustomBuyer = variables.entities.entities.some(
        (e) => e.entityType === LeadEntityType.CustomOrganisation,
      );
      logEvent(EventNames.leadCreated, {
        "Lead ID": id,
        "Lead title": name,
        "Lead value": value,
        "Lead stage": stage.name,
        "Free-text buyer": hasCustomBuyer,
        ...trackingData,
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
  return { data: data?.createLead, ...rest };
}
